import React, { useEffect, useMemo, useState } from 'react';
import { Skeleton } from 'antd';

import API from 'src/apis';
import { PROMPT_SUGGESTIONS } from 'src/common/constants';

import LandingIcon from 'src/assets/images/landing_page.gif';
import QueryInput from 'src/components/atoms/query_input';
import MobileNavigation from 'src/components/organisms/mobile_navigation';
import TatvaCareLogo from 'src/assets/images/tatvacare_logo.png';
import ZydusLogo from 'src/assets/images/zydus_logo.svg';

import styles from './index.module.scss';

interface LandingComponentProps {
    onPressStartStream: () => void;
    onPressSuggetion: (query: string) => void;
};

const LandingComponent: React.FunctionComponent<LandingComponentProps> = props => {
    const { onPressStartStream, onPressSuggetion } = props;
    const [width, setWidth] = useState(window.innerWidth);
    const [loading, setLoading] = useState(true);
    const [logoLoading, setLogoLoading] = useState(true);

    const [prompts, setPrompts] = useState<string[]>([]);

    useEffect(() => {
        const handleResize = () => {
            setWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        getPrompts();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const getPrompts = async () => {
        setLoading(true);
        try {
            const suggestedPrompts = await API.GetSuggestedPrompts();
            if (suggestedPrompts.status === 'success' && suggestedPrompts?.data?.prompts?.length) {
                setPrompts(suggestedPrompts?.data?.prompts);
            } else {
                setPrompts(PROMPT_SUGGESTIONS);
            }
        } catch (error) {
            console.log(error);
            setPrompts(PROMPT_SUGGESTIONS);
        } finally {
            setLoading(false);
        }
    };

    const logoSrc = useMemo(() => {
        const userDetails = localStorage.getItem('userDetails');
        const fromVendor = localStorage.getItem('from_vendor');
        return fromVendor && userDetails
            ? JSON.parse(userDetails)?.service_payer_logo || ZydusLogo
            : TatvaCareLogo;
    }, [localStorage.getItem('userDetails')]);

    const appType = useMemo(() => {
        const app = localStorage.getItem('app');
        return app;
    }, [localStorage.getItem('app')]);

    return (
        <div className={styles.landing_component_wrapper}>

            <div className={styles.landing_component_content}>
                {width > 900 && <img src={LandingIcon} alt='landing_icon' className={styles.landing_icon} />}

                <div className={styles.landing_component_heading}>{`Welcome to ${appType === 'zymedelite' ? 'ZyMedAI' : appType === 'ask_tatva' ? 'Ask Tatva' : 'Tatva Ai'}`}</div>
                <div className={styles.landing_component_desc}>Revolutionize Your Research with Every Query</div>
                {appType === 'tatva_ai' && <div className={styles.landing_component_logo_wrapper}>
                    <span className={styles.landing_component_logo_text}>Powered by</span>
                    <div style={{ height: '19px', width: 'auto' }}>
                        {logoLoading && <Skeleton.Input size='small' active />}
                        <img
                            src={logoSrc}
                            alt={'tatva_logo'}
                            height="19px"
                            style={{ display: logoLoading ? 'none' : 'block' }}
                            onLoad={() => setLogoLoading(false)}
                            onError={(e: any) => { e.target.src = TatvaCareLogo; }}
                        />
                    </div>
                </div>}

                <div className={styles.landing_component_content_box}>
                    <div className={styles.content_desc}>
                        {`${appType === 'zymedelite' ? 'ZyMedAI' : appType === 'ask_tatva' ? 'Ask Tatva' : 'Tatva-ai'}`} exclusively offers peer-reviewed, medically relevant content from PubMed, ensuring unparalleled accuracy and relevance for medical professionals. Its advanced AI technology significantly enhances search precision, making it an indispensable tool for informed medical decisions and research
                    </div>
                </div>


                {loading ? (
                    <div className={styles.landing_suggestion_boxs}>
                        {[...Array(width < 900 ? 3 : 4)].map((_, index) => (
                            <React.Fragment key={index}>
                                <Skeleton.Node active={true} style={{ width: 160 }} />
                                <Skeleton.Node active={true} style={{ width: 160 }} />
                            </React.Fragment>
                        ))}
                    </div>
                ) : !!prompts.length && (
                    <div className={styles.landing_suggestion_boxs}>
                        {prompts.slice(0, 8).map((suggestion, index) => (
                            <div
                                key={index}
                                className={styles.landing_suggestion_box}
                                onClick={() => onPressSuggetion(suggestion)}
                                style={{ '--animation-delay': `${index * 0.1}s` } as React.CSSProperties}
                            >
                                <span className={styles.landing_suggestion_box_question}>{suggestion}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className={styles.query_input_wrapper}>
                <QueryInput onPressStartStream={onPressStartStream} />
            </div>

            {width < 900 &&
                <div>
                    <MobileNavigation />
                </div>
            }
        </div>
    )
}

export default LandingComponent;